import { useSyncExternalStore } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  FAVORIS_MAX, validerCheminFavori, validerLibelleFavori, normaliserLibelleFavori,
} from '@gestion/shared/favoris'
import { adminMutate, apiRead, type ToastFn } from '../admin/primitives.js'
import { activeChainFor, familyForPath } from '../navigation/site-navigation.js'

/**
 * FAVORIS DE LA BARRE LATÉRALE — le store du navigateur (lot 5, 24/10/2026).
 *
 * La vérité vit en base (/api/v1/me/favorites). Ici, une copie EN MÉMOIRE
 * seulement : jamais de localStorage ni de sessionStorage. Sur un poste
 * partagé du club, le compte suivant y lirait sinon les noms épinglés par le
 * précédent ; la déconnexion recharge le document, la mémoire part avec.
 *
 * Écritures : uniquement `adminMutate` (session + CSRF, reprise après
 * step-up), vers /api/v1/me/favorites et rien d'autre. Elles passent l'une
 * après l'autre (file d'attente) : deux « Monter » rapides se suivent au lieu
 * de se croiser, chacune sur l'ordre laissé par la précédente.
 *
 * Relecture : au retour sur l'onglet après plus de 30 s, et sur le signal
 * `BroadcastChannel('gestion.favoris')` qu'un autre onglet émet après chacune
 * de ses écritures. Le signal ne porte AUCUNE donnée : chaque onglet relit
 * avec sa propre session. Après une première lecture manquée, un clic sur
 * l'étoile relit aussi (`reessayerFavoris`).
 *
 * Le favori ne donne aucun droit : la garde de route et l'API restent
 * l'arbitre. La grisure d'un favori devenu interdit n'est qu'un indice tiré du
 * registre (`permissionDeFavori`), la même règle que celle qui masque les
 * modules de la barre.
 */

export interface Favori {
  id: string
  path: string
  label: string
  position: number
  created_at: string
}

export type StatutFavoris = 'vide' | 'chargement' | 'pret' | 'erreur'

export interface EtatFavoris {
  userId: string | null
  statut: StatutFavoris
  liste: Favori[]
  max: number
  /** Section repliée, commune au rail et au tiroir ; éphémère, comme les familles. */
  replie: boolean
}

const VIDE: EtatFavoris = { userId: null, statut: 'vide', liste: [], max: FAVORIS_MAX, replie: false }

let etat: EtatFavoris = VIDE
const abonnes = new Set<() => void>()
const poser = (suite: Partial<EtatFavoris>) => {
  etat = { ...etat, ...suite }
  for (const a of abonnes) a()
}

export function useFavoris(): EtatFavoris {
  return useSyncExternalStore(
    (cb) => { abonnes.add(cb); return () => { abonnes.delete(cb) } },
    () => etat,
  )
}

/** L'instantané, pour qui le lit hors rendu (un menu, à son ouverture). */
export function etatFavoris(): EtatFavoris {
  return etat
}

/** Le favori de cette adresse exacte, s'il existe. */
export function favoriDe(path: string): Favori | undefined {
  return etat.liste.find((f) => f.path === path)
}

export function replierFavoris(replie: boolean): void {
  poser({ replie })
}

/** Le droit le plus profond que le registre demande pour cette adresse. */
export function permissionDeFavori(path: string): string | undefined {
  const chaine = activeChainFor(path)
  for (let i = chaine.length - 1; i >= 0; i--) {
    const p = chaine[i]!.permission
    if (p) return p
  }
  return undefined
}

/** L'icône du registre : la page la plus précise, sinon sa famille. */
export function iconeDeFavori(path: string): LucideIcon {
  return activeChainFor(path).at(-1)?.icon ?? familyForPath(path).icon
}

/* ─── lecture ─── */

const RELIRE_APRES_MS = 30_000
let generation = 0
let luA = 0
let canal: BroadcastChannel | null = null

const trier = (l: Favori[]) => [...l].sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at))

async function relire(): Promise<void> {
  const userId = etat.userId
  if (!userId) return
  const g = ++generation
  luA = Date.now()
  // Une relecture de fond qui échoue garde la liste connue : l'étoile et la
  // section restent utilisables, une écriture dira elle-même si le serveur
  // ne répond plus. Seule une PREMIÈRE lecture manquée passe en erreur.
  const echec = () => { if (g === generation && etat.userId === userId && etat.statut !== 'pret') poser({ statut: 'erreur' }) }
  try {
    const r = await apiRead<{ authenticated?: boolean; max?: number; favorites?: Favori[] }>('/api/v1/me/favorites')
    // Une réponse dépassée (autre compte, relecture plus récente) ne s'écrit pas.
    if (g !== generation || etat.userId !== userId) return
    if (r.status !== 200 || !Array.isArray(r.data.favorites)) { echec(); return }
    poser({ statut: 'pret', liste: trier(r.data.favorites), max: r.data.max ?? FAVORIS_MAX })
  } catch {
    echec()
  }
}

/**
 * Une nouvelle lecture après une première lecture manquée (statut 'erreur'),
 * sur un GESTE seulement : un clic sur l'étoile, jamais un rendu ni un effet.
 * Le statut repasse à 'chargement' le temps de la lecture : un second clic
 * n'en lance pas une autre, et un nouvel échec rend 'erreur' sans rien
 * relancer de lui-même. Pas de boucle.
 */
export function reessayerFavoris(): void {
  if (etat.statut !== 'erreur' || !etat.userId) return
  poser({ statut: 'chargement' })
  void relire()
}

const auRetour = () => {
  if (document.visibilityState === 'visible' && Date.now() - luA > RELIRE_APRES_MS) void relire()
}

/** Charge les favoris du compte connecté et écoute les autres onglets. */
export function chargerFavoris(userId: string): void {
  if (etat.userId !== userId) {
    // Jamais la liste d'un autre compte, même le temps d'une lecture.
    generation++
    etat = { ...VIDE, replie: etat.replie }
    poser({ userId, statut: 'chargement' })
  }
  if (!canal && typeof BroadcastChannel !== 'undefined') {
    canal = new BroadcastChannel('gestion.favoris')
    canal.onmessage = () => { void relire() }
  }
  document.addEventListener('visibilitychange', auRetour)
  void relire()
}

/** Plus de session : la liste part, les écoutes aussi. */
export function viderFavoris(): void {
  generation++
  document.removeEventListener('visibilitychange', auRetour)
  canal?.close()
  canal = null
  poser({ ...VIDE, replie: etat.replie })
}

/** La liste après une écriture réussie : une relecture partie AVANT elle
 *  rapporterait l'ancienne, elle est donc écartée à son arrivée. */
function poserListe(liste: Favori[]): void {
  generation++
  poser({ liste: trier(liste), statut: 'pret' })
}

/** Prévient les autres onglets, sans rien leur dire de plus. */
const signaler = () => { try { canal?.postMessage(null) } catch { /* canal fermé */ } }

/* ─── écritures ─── */

const URL_FAVORIS = '/api/v1/me/favorites'
const HORS_LIGNE = 'Le serveur ne répond pas : vos favoris n’ont pas changé.'
/** 429 : le message du limiteur est en anglais, il ne s'affiche jamais. */
const TROP_VITE = 'Trop de changements d’un coup : réessayez dans une minute.'

/** Le message d'un refus : le nôtre sur un 429, sinon celui du serveur. */
const refus = (r: { status: number; data: Reponse }, repli: string): string =>
  r.status === 429 ? TROP_VITE : (r.data.message ?? repli)

let file: Promise<unknown> = Promise.resolve()
/** Une écriture après l'autre. */
function enFile<T>(op: () => Promise<T>): Promise<T> {
  const suite = file.then(op, op)
  file = suite.catch(() => undefined)
  return suite
}

type Reponse = { error?: string; message?: string; favorite?: Favori; created?: boolean; favorites?: Favori[] }

async function ecrire(method: 'POST' | 'DELETE' | 'PUT', chemin: string, corps?: unknown): Promise<{ status: number; data: Reponse } | null> {
  try { return await adminMutate<Reponse>(method, chemin, corps) } catch { return null }
}

/** Le nom qui partira : normalisé, et jamais vide. */
export function nomDeFavori(brut: string | null | undefined, repli: string): string {
  const nom = normaliserLibelleFavori(brut ?? '')
  return validerLibelleFavori(nom).ok ? nom : normaliserLibelleFavori(repli)
}

/** Ajoute une page. Rend le favori (créé ou déjà là), ou null. */
export function ajouterFavori(f: { path: string; label: string }, toast: ToastFn, opts: { silencieux?: boolean } = {}): Promise<Favori | null> {
  return enFile(async () => {
    const chemin = validerCheminFavori(f.path)
    if (!chemin.ok) { toast('error', chemin.raison); return null }
    const nom = normaliserLibelleFavori(f.label)
    const libelle = validerLibelleFavori(nom)
    if (!libelle.ok) { toast('error', libelle.raison); return null }
    const r = await ecrire('POST', URL_FAVORIS, { path: chemin.chemin, label: libelle.libelle })
    if (!r) { toast('error', HORS_LIGNE); return null }
    if ((r.status === 201 || r.status === 200) && r.data.favorite) {
      const fav = r.data.favorite
      if (!etat.liste.some((x) => x.id === fav.id)) poserListe([...etat.liste, fav])
      if (r.status === 201) signaler()
      if (!opts.silencieux) {
        toast('ok', r.status === 201 ? `Ajouté aux favoris : ${fav.label}.` : `Déjà dans vos favoris : ${fav.label}.`)
      }
      return fav
    }
    if (r.data.error === 'FAVORITES_FULL') void relire()
    toast('error', refus(r, 'Ajout refusé par le serveur.'))
    return null
  })
}

/** Retire un favori, avec « Annuler » dans le toast : il revient à sa place. */
export function retirerFavori(f: Favori, toast: ToastFn): Promise<boolean> {
  return enFile(async () => {
    const ordreAvant = etat.liste.map((x) => x.id)
    const r = await ecrire('DELETE', `${URL_FAVORIS}/${encodeURIComponent(f.id)}`)
    if (!r) { toast('error', HORS_LIGNE); return false }
    if (r.status === 404) {
      void relire()
      toast('error', r.data.message ?? 'Ce favori n’existe pas ou plus.')
      return false
    }
    if (r.status !== 200) { toast('error', refus(r, 'Retrait refusé par le serveur.')); return false }
    poserListe(etat.liste.filter((x) => x.id !== f.id).map((x, i) => ({ ...x, position: i })))
    signaler()
    toast('ok', `Retiré des favoris : ${f.label}.`, {
      action: { label: 'Annuler', onAction: () => { void restaurer(f, ordreAvant, toast) } },
    })
    return true
  })
}

/** « Annuler » un retrait : le même favori, à la même place si la liste le permet. */
async function restaurer(f: Favori, ordreAvant: string[], toast: ToastFn): Promise<void> {
  const remis = await ajouterFavori({ path: f.path, label: f.label }, toast, { silencieux: true })
  if (!remis) return
  await enFile(async () => {
    const presents = new Set(etat.liste.map((x) => x.id))
    const voulu = ordreAvant.map((id) => (id === f.id ? remis.id : id)).filter((id) => presents.has(id))
    // Les favoris ajoutés entre-temps ailleurs gardent leur place, en dernier.
    for (const x of etat.liste) if (!voulu.includes(x.id)) voulu.push(x.id)
    if (voulu.some((id, i) => etat.liste[i]?.id !== id)) {
      const r = await ecrire('PUT', `${URL_FAVORIS}/order`, { ids: voulu })
      if (r?.status === 200 && Array.isArray(r.data.favorites)) { poserListe(r.data.favorites); signaler() }
      else void relire()
    }
  })
  toast('ok', `${remis.label} est de retour dans vos favoris.`)
}

/**
 * Monte (-1) ou descend (+1) un favori d'un cran. `visible` : l'échange se
 * fait avec le voisin VISIBLE (la barre ne montre pas une adresse refusée) ;
 * les autres gardent leur place. Sans voisin, rien n'est écrit.
 */
export function deplacerFavori(id: string, sens: -1 | 1, toast: ToastFn, visible: (f: Favori) => boolean = () => true): Promise<boolean> {
  return enFile(async () => {
    const ids = etat.liste.map((x) => x.id)
    const i = ids.indexOf(id)
    let j = i + sens
    while (i >= 0 && j >= 0 && j < ids.length && !visible(etat.liste[j]!)) j += sens
    if (i < 0 || j < 0 || j >= ids.length) return false
    ;[ids[i], ids[j]] = [ids[j]!, ids[i]!]
    const r = await ecrire('PUT', `${URL_FAVORIS}/order`, { ids })
    if (!r) { toast('error', HORS_LIGNE); return false }
    if (r.status === 200 && Array.isArray(r.data.favorites)) {
      poserListe(r.data.favorites)
      signaler()
      return true
    }
    if (r.data.error === 'FAVORITES_CHANGED') {
      await relire()
      toast('error', 'Vos favoris ont changé dans un autre onglet : la liste a été relue.')
      return false
    }
    toast('error', refus(r, 'Rangement refusé par le serveur.'))
    return false
  })
}
